import type { ComponentType } from 'preact';
import { useEffect } from 'preact/hooks';
import { Router, Route, route } from 'preact-router';
import { token, isAdmin, isSuperadmin } from './store/state';

import { LoginView } from './views/LoginView';
import { RegistroView } from './views/RegistroView';
import { DashboardView } from './views/DashboardView';
import { AgendaView } from './views/AgendaView';
import { ClientesView } from './views/ClientesView';
import { ClienteHistorialView } from './views/ClienteHistorialView';
import { ClienteFormView } from './views/ClienteFormView';
import { NuevaVisitaView } from './views/NuevaVisitaView';
import { VisitaView } from './views/VisitaView';
import { ReasignarView } from './views/ReasignarView';
import { PosponerFormView } from './views/PosponerFormView';
import { FallidaFormView } from './views/FallidaFormView';
import { CancelarVisitaView } from './views/CancelarVisitaView';
import { PerfilView } from './views/PerfilView';
import { TecnicoFormView } from './views/TecnicoFormView';
import { EditarTecnicoView } from './views/EditarTecnicoView';
import { AdminFormView } from './views/AdminFormView';
import { EditarPerfilView } from './views/EditarPerfilView';
import { SuperadminView } from './views/SuperadminView';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withAuth<P extends Record<string, any>>(Component: ComponentType<P>) {
  return function Guarded(props: P) {
    useEffect(() => {
      if (!token.value) {
        route('/login', true);
      }
    }, [token.value]);
    if (!token.value) return null;
    return <Component {...props} />;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withAdmin<P extends Record<string, any>>(Component: ComponentType<P>) {
  return withAuth(function AdminGuarded(props: P) {
    useEffect(() => {
      if (!isAdmin.value) route('/agenda', true);
    }, [isAdmin.value]);
    if (!isAdmin.value) return null;
    return <Component {...props} />;
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withSuperadmin<P extends Record<string, any>>(Component: ComponentType<P>) {
  return withAuth(function SuperadminGuarded(props: P) {
    useEffect(() => {
      if (!isSuperadmin.value) route('/agenda', true);
    }, [isSuperadmin.value]);
    if (!isSuperadmin.value) return null;
    return <Component {...props} />;
  });
}

function HomeRedirect() {
  useEffect(() => {
    if (!token.value) {
      route('/login', true);
    } else if (isSuperadmin.value) {
      route('/plataforma', true);
    } else {
      route(isAdmin.value ? '/dashboard' : '/agenda', true);
    }
  }, []);
  return null;
}

export function AppRouter() {
  return (
    <Router>
      <Route path="/" component={HomeRedirect} />
      <Route path="/login" component={LoginView} />
      <Route path="/registro" component={RegistroView} />

      <Route path="/dashboard" component={withAdmin(DashboardView)} />
      <Route path="/clientes" component={withAdmin(ClientesView)} />
      <Route path="/clientes/nuevo" component={withAdmin(ClienteFormView)} />
      <Route path="/clientes/:id/editar" component={withAdmin(ClienteFormView)} />
      <Route path="/clientes/:id" component={withAuth(ClienteHistorialView)} />

      <Route path="/agenda" component={withAuth(AgendaView)} />
      <Route path="/visitas/nueva" component={withAuth(NuevaVisitaView)} />
      <Route path="/visitas/:id/reasignar" component={withAuth(ReasignarView)} />
      <Route path="/visitas/:id/posponer" component={withAuth(PosponerFormView)} />
      <Route path="/visitas/:id/fallida" component={withAuth(FallidaFormView)} />
      <Route path="/visitas/:id/cancelar" component={withAuth(CancelarVisitaView)} />
      <Route path="/visitas/:id" component={withAuth(VisitaView)} />

      <Route path="/perfil" component={withAuth(PerfilView)} />
      <Route path="/perfil/editar" component={withAuth(EditarPerfilView)} />
      <Route path="/perfil/tecnicos/nuevo" component={withAdmin(TecnicoFormView)} />
      <Route path="/perfil/tecnicos/:id/editar" component={withAdmin(EditarTecnicoView)} />
      <Route path="/perfil/admins/nuevo" component={withAdmin(AdminFormView)} />

      <Route path="/plataforma" component={withSuperadmin(SuperadminView)} />

      <Route default component={withAuth(AgendaView)} />
    </Router>
  );
}
